import type { OfficePayload } from "@/api/endpoints/doctor/offices"
import type { APIError } from "@/types/common"
import type { Office } from "@/types/offices"
import { acceptHMRUpdate, defineStore } from "pinia"
import Api from "@/api"

/** Normalizes an API error to its response payload when present, then rethrows. */
function unwrapApiError(err: unknown): never {
	const error = err as APIError
	throw error.response?.data || error
}

export const useOfficesStore = defineStore("offices", {
	state: () => ({
		list: [] as Office[],
		loading: false
	}),
	getters: {
		/** Keeps the preferred office when it still exists, otherwise falls back to the first available one. */
		resolveOfficeId:
			state =>
			(preferredId?: number | null): number | null => {
				if (preferredId != null && state.list.some(office => office.id === preferredId)) {
					return preferredId
				}

				return state.list[0]?.id ?? null
			}
	},
	actions: {
		async fetchOffices(force = false): Promise<Office[]> {
			if (!force && (this.list.length || this.loading)) {
				return this.list
			}

			this.loading = true

			try {
				const response = await Api.doctor.offices.list()
				const data = response.data ?? []

				this.list = data
				return data
			} catch (err) {
				unwrapApiError(err)
			} finally {
				this.loading = false
			}
		},

		async createOffice(payload: OfficePayload): Promise<Office> {
			try {
				const response = await Api.doctor.offices.create(payload)
				const office = response.data

				this.list = [...this.list, office]
				return office
			} catch (err) {
				unwrapApiError(err)
			}
		},

		async updateOffice(officeId: number, payload: OfficePayload): Promise<Office> {
			try {
				const response = await Api.doctor.offices.update(officeId, payload)
				const office = response.data

				this.list = this.list.map(item => (item.id === officeId ? office : item))
				return office
			} catch (err) {
				unwrapApiError(err)
			}
		},

		async deleteOffice(officeId: number): Promise<void> {
			try {
				await Api.doctor.offices.delete(officeId)
				this.list = this.list.filter(item => item.id !== officeId)
			} catch (err) {
				unwrapApiError(err)
			}
		}
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useOfficesStore, import.meta.hot))
}
